import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useSubscriptionContext } from '../../../contexts/SubscriptionContext';
import { useCreateWhatsAppConfig, useWhatsAppConfig } from '../hooks/useWhatsAppConfig';
import { useTenantState } from '../../../hooks/useTenantState';
import { StepPhoneNumber } from './StepPhoneNumber';
import { StepAgentSettings } from './StepAgentSettings';
import type { WhatsAppConfigFormData } from '../types/whatsapp.types';

type WizardStage = 0 | 1 | 2;

const defaultAgentSettings = {
  agent_name: 'Assistant FAQ',
  welcome_message: 'Bienvenue sur notre service FAQ WhatsApp.',
  escalation_message: 'Je vous mets en relation avec un conseiller.',
};

export const ConfigWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSubscription } = useSubscriptionContext();
  const { data: existingConfig, isLoading, refetch } = useWhatsAppConfig();
  const createConfigMutation = useCreateWhatsAppConfig();

  const [activeStep, setActiveStep] = useState<WizardStage>(0);
  const [formData, setFormData] = useState<Partial<WhatsAppConfigFormData>>(defaultAgentSettings);
  const [completedSteps, setCompletedSteps] = useTenantState<number[]>('whatsapp-wizard-completed', []);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRedirected = useRef(false);
  const isEditing = new URLSearchParams(location.search).get('edit') === '1';

  const stepLabels = useMemo(
    () => [
      {
        title: 'Numéro WhatsApp Business',
        description: 'Renseignez le numéro professionnel du client.',
        icon: <WhatsAppIcon color="primary" />,
      },
      {
        title: 'Paramètres de l’assistant',
        description: 'Ajustez le nom et les messages par défaut si besoin.',
        icon: <SmartToyIcon color="secondary" />,
      },
      {
        title: 'Validation et activation',
        description: 'On crée la configuration et la plateforme prépare le raccordement Meta en arrière-plan.',
        icon: <SettingsIcon color="action" />,
      },
    ],
    []
  );

  useEffect(() => {
    if (existingConfig?.business_phone_number && !isEditing && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/whatsapp/settings');
    }
  }, [existingConfig, isEditing, navigate]);

  useEffect(() => {
    if (!existingConfig || !isEditing) return;

    setFormData((current) => ({
      ...current,
      business_phone_number: existingConfig.business_phone_number || '',
      agent_name: existingConfig.agent_name || defaultAgentSettings.agent_name,
      welcome_message: existingConfig.welcome_message || defaultAgentSettings.welcome_message,
      escalation_message: existingConfig.escalation_message || defaultAgentSettings.escalation_message,
    }));
  }, [existingConfig, isEditing]);

  const markStepComplete = (stepIndex: number, stepData: Partial<WhatsAppConfigFormData>) => {
    setErrorMessage(null);
    setFormData((current) => ({ ...current, ...stepData }));
    const currentCompleted = Array.isArray(completedSteps) ? completedSteps : [];
    setCompletedSteps(
      currentCompleted.includes(stepIndex) ? currentCompleted : [...currentCompleted, stepIndex]
    );

    if (stepIndex < stepLabels.length - 1) {
      setActiveStep((current) => ((current + 1) as WizardStage));
    }
  };

  const handleBack = () => {
    setErrorMessage(null);
    setActiveStep((current) => ((Math.max(0, current - 1)) as WizardStage));
  };

  const handleFinish = async () => {
    setErrorMessage(null);

    if (!formData.business_phone_number) {
      setErrorMessage('Le numéro WhatsApp Business est requis pour continuer.');
      setActiveStep(0);
      return;
    }

    const payload: WhatsAppConfigFormData = {
      subscription_id: currentSubscription.id,
      business_phone_number: formData.business_phone_number,
      api_token: formData.api_token || '',
      phone_number_id: formData.phone_number_id || '',
      meta_business_id: formData.meta_business_id || '',
      meta_app_id: formData.meta_app_id || '',
      agent_name: formData.agent_name || defaultAgentSettings.agent_name,
      welcome_message: formData.welcome_message || defaultAgentSettings.welcome_message,
      escalation_message: formData.escalation_message || defaultAgentSettings.escalation_message,
    };

    try {
      const result = await createConfigMutation.mutateAsync(payload);
      setCompletedSteps([0, 1, 2]);
      setFormData((current) => ({ ...current, ...payload, config_id: result?.id }));
      await refetch();
      navigate('/whatsapp/settings');
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          'La création de la configuration a échoué. Réessayez dans un instant.'
      );
    }
  };

  if (!currentSubscription) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Chargement de votre abonnement...
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          Vérification de la configuration WhatsApp...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Paper
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(16,185,129,0.08))',
        }}
      >
        <Stack spacing={1.5}>
          <Chip
            icon={<CheckIcon fontSize="small" />}
            label="Parcours guidé"
            color="success"
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
          />
          <Typography variant="h4" fontWeight={700}>
            Configuration WhatsApp Business
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Nous gardons le parcours simple: le client saisit son numéro professionnel, ajuste les
            paramètres de l’assistant si besoin, puis DigitalCloud360 prépare le raccordement Meta
            en arrière-plan.
          </Typography>
        </Stack>
      </Paper>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Les informations Meta peuvent être complétées plus tard si nécessaire. Le service est pensé
        pour être activé progressivement, sans bloquer la souscription sur des champs techniques
        manquants.
      </Alert>

      <Stepper activeStep={activeStep} orientation="vertical">
        {stepLabels.map((step, index) => {
          const isCompleted = completedSteps.includes(index);
          const isCurrent = activeStep === index;

          return (
            <Step key={step.title} completed={isCompleted}>
              <StepLabel icon={step.icon}>
                <Typography variant="h6">{step.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                {index === 0 && (
                  <StepPhoneNumber
                    formData={formData}
                    isActive={isCurrent}
                    onComplete={(data) => markStepComplete(index, data)}
                    onNext={() => setActiveStep(1)}
                    onBack={index > 0 ? handleBack : undefined}
                  />
                )}

                {index === 1 && (
                  <StepAgentSettings
                    formData={formData}
                    isActive={isCurrent}
                    onComplete={(data) => markStepComplete(index, data)}
                    onNext={() => setActiveStep(2)}
                    onBack={handleBack}
                  />
                )}

                {index === 2 && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Numéro WhatsApp Business
                          </Typography>
                          <Typography variant="h6">
                            {formData.business_phone_number || 'Non renseigné'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Assistant
                          </Typography>
                          <Typography variant="body1">
                            {formData.agent_name || defaultAgentSettings.agent_name}
                          </Typography>
                        </Box>
                        <Divider />
                        <Alert severity="success">
                          La configuration sera créée avec les informations disponibles, puis
                          DigitalCloud360 poursuivra le raccordement technique côté Meta.
                        </Alert>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {index === 2 && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleBack}
                      disabled={createConfigMutation.isLoading}
                    >
                      Retour
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        createConfigMutation.isLoading ? <CircularProgress size={18} /> : null
                      }
                      onClick={handleFinish}
                      disabled={createConfigMutation.isLoading}
                    >
                      {createConfigMutation.isLoading ? 'Création en cours...' : 'Créer la configuration'}
                    </Button>
                  </Stack>
                )}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Ce que le client voit
          </Typography>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              1. Il saisit son numéro WhatsApp professionnel.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              2. Il peut personnaliser le nom et les messages de l’assistant.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              3. DigitalCloud360 crée la base de configuration et prépare la connexion Meta.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              4. Le suivi se fait ensuite dans la page de statut WhatsApp.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
